import { ChangeDetectionStrategy, Component, computed, OnInit, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { SettingsEudrService } from "@components/settings/settings-extensions/settings-eudr/settings-eudr.service";
import { ConfirmDialogComponent, InfoDialogComponent } from "@components/shared";
import { IconConstants, TextConstants } from "@shared/constants";
import { ConfirmDialogResponseEnum, CustomFieldsResourceTypeEnum, RouteEnum } from "@shared/enums";
import { ICustomField, IEudrCredentials } from "@shared/interfaces";
import { EudrDds } from "@shared/interfaces/eudr-dds.interface";
import { NotificationService, AuthenticationService, EudrExtensionService } from "@shared/services";

import { EudrConnectDialogComponent } from ".";

@Component({
  standalone: false,
  selector: "app-settings-eudr",
  templateUrl: "./settings-eudr.component.html",
  styleUrl: "./settings-eudr.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsEudrComponent implements OnInit {
  public isLoading = signal(true);

  public eudrCredentials = signal<IEudrCredentials>(undefined);

  public statements = signal<EudrDds.DdsDetails[]>([]);

  public readonly routingEnum = RouteEnum;

  public readonly isAccountOwnerOrAdmin = this.authenticationService.isAccountOwnerOrAdmin();

  public isDdsServiceUnavailable = false;

  public readonly serviceUnavailableMsg = TextConstants.SERVICE_UNAVAILABLE;

  public isServiceLoading = computed(() => this.settingsEudrService.isLoading());

  public isAnyLoading = computed(() => this.isServiceLoading() || this.isLoading());

  public readonly translations: any = {
    title: $localize`:@@shared.eudrincapitalletters:EUDR`,
    titleBackText: TextConstants.BACK_TO_ADMIN,
    optionsTp: $localize`:@@settings-eudr.options:Options`,
    missingCustomFieldsTp: $localize`:@@settings-eudr.help:Some custom fields that are required for EUDR integration are not present. Click here to add them`,
  };

  constructor(
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private eudrExtensionService: EudrExtensionService,
    public settingsEudrService: SettingsEudrService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.settingsEudrService.reset();

    await Promise.all([this.settingsEudrService.setMissingCustomFields(), this.getCredentials()]);

    this.isLoading.set(false);
  }

  private getCredentials = async (): Promise<void> => {
    try {
      const credentials = await this.eudrExtensionService.get();

      this.eudrCredentials.set(credentials);
    } catch (error) {
      if (error.status === 503) {
        this.isDdsServiceUnavailable = true;
      } else if (error.status !== 404) {
        this.notificationService.showError(error);
      }
    }
  };

  public get missingCustomFieldGroups(): {
    resourceType: CustomFieldsResourceTypeEnum;
    labels: string[];
  }[] {
    return this.settingsEudrService.missingCustomFieldGroups;
  }

  public get allCustomFields(): ICustomField[] {
    return this.settingsEudrService.allCustomFields;
  }

  public async onOpenMissingCustomFieldsDialog(): Promise<void> {
    this.isLoading.set(true);

    try {
      await this.settingsEudrService.onOpenMissingCustomFieldsDialog();
    } finally {
      this.isLoading.set(false);
    }
  }

  public onTestConnection = async (): Promise<void> => {
    try {
      const credentials = this.eudrCredentials();
      const payload = {
        username: credentials.username,
        authKey: credentials.authKey,
      };

      await this.eudrExtensionService.ping(payload);
      this.notificationService.showSuccess(
        $localize`:@@settings-eudr.connectiontestsuccessful:Connection test successful`,
      );
    } catch (error) {
      let contentText = $localize`:@@error.eudr.couldnotconnect:We couldn't establish a connection with the EU Information System using the stored credentials. Please ensure the validity of the credentials or try later.`;

      if (error?.code === 503) {
        contentText = TextConstants.SERVICE_UNAVAILABLE;
      }
      this.dialog.open(InfoDialogComponent, {
        data: {
          icon: "error",
          iconColor: "red",
          title: $localize`:@@error.settings-eudr.connectiontestfailed:Connection test failed`,
          contentText,
        },
      });
    }
  };

  public onRemoveConnection = (): void => {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.REMOVE_CONFIRMATION,
        contentText: $localize`:@@confirm.settings-eudr.removecredentials:This will remove your EU IS credentials from Interu, but will keep all EUDR DDS records. Would you like to proceed?`,
        confirmButtonText: TextConstants.REMOVE,
        confirmButtonColor: "danger",
        confirmButtonIcon: IconConstants.DELETE,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: ConfirmDialogResponseEnum) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        this.isLoading.set(true);
        try {
          await this.eudrExtensionService.delete();
          this.eudrCredentials.set(undefined);
          this.notificationService.showSuccess(
            $localize`:@@settings-eudr.connectionremoved:Connection removed`,
          );
        } catch (error) {
          this.notificationService.showError(error);
        } finally {
          this.isLoading.set(false);
        }
      }
    });
  };

  public onOpenConnectDialog = (): void => {
    const dialogRef = this.dialog.open(EudrConnectDialogComponent, {
      data: { eudrCredentials: this.eudrCredentials() },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { hasSaved: boolean; credentials: IEudrCredentials }) => {
        if (result?.hasSaved) {
          this.eudrCredentials.set(result.credentials);
        }
      });
  };
}
